import { v4 as uuidV4 } from 'uuid';

import { approximateDuration } from 'my-core/date-utils';
import { pluralize } from 'my-core/my-utils';
import { palette } from 'my-theme/myTheme';

import { COURSE_LESSON_TYPES_CONFIG, getLessonIcon } from './course-lesson-utils';
import { cloneSkillAssociations } from './mastery-taxonomy-utils';
import { getQuizIcon, QUESTION_CONTENT_FIELDS, QUIZ_TYPES_CONFIG } from './quiz-utils';

import {
  faChalkboardTeacher,
  faClapperboardPlay,
  faClipboardListCheck,
  faClipboardQuestion,
  faFileCircleInfo,
  faFlagCheckered,
  faLightbulbOn,
  faSealQuestion,
} from '@fortawesome/pro-solid-svg-icons';

export function cloneActivity(type, activity, userId, forceIncludeVideo) {
  const clonedActivity = {
    ...activity,
    id: undefined,
    clientId: uuidV4(),
    content_skill_tags: cloneSkillAssociations(activity.content_skill_tags),
    course_usage_stats: { count: 0, recent: [] },
    starred: undefined,
    synced_from_id: activity.id,
    synced_from_type: activity.id && type, // TODO: track synced_from even if its a new activity
    synced_at: activity.id && new Date(),
    user_id: userId,
    ...(!forceIncludeVideo && (type === 'CourseLesson' || type === 'QuizQuestion') && userId !== activity.user_id ?
      {
        video: undefined,
        elements: undefined,
        video_elements: undefined,
      }
    : {}),
  };
  if (type === 'QuizQuestion') clonedActivity.quiz_usage_stats = { count: 0, recent: [] };
  if (type === 'Quiz') {
    clonedActivity.elements = (activity.elements || []).map(el => ({ ...el, id: undefined, clientId: uuidV4() }));
  }
  return clonedActivity;
}

export function createOcActivity(activityType, activity, defaultOcActivityProps) {
  const clientId = uuidV4();
  const activityId = (activity && (activity.id || activity.clientId)) || uuidV4();
  return {
    clientId,
    activity_type: activityType,
    activity_id: activityId,
    title: getActivityDefaultOCActivityTitle(activity) || `New ${ACTIVITY_TYPES_CONFIG[activityType].label}`,
    activity_metadata: getMetadataUpdates(activityType, activity) || {},
    ...defaultOcActivityProps,
  };
}

export function getActivityDefaultOCActivityTitle(activity) {
  return (
    activity?.course_usage_stats?.recent?.find(cu => cu.activity?.label)?.activity?.label ||
    activity?.title ||
    activity?.description
  );
}

export function getMetadataUpdates(type, attrs = {}) {
  switch (type) {
    case 'CourseLesson':
      return [
        ['lesson_type', attrs.type],
        ['reading_only', attrs.reading_only],
        ['duration', attrs.video && attrs.video.duration],
        ['is_private', attrs.is_private],
      ].reduce(getMetadatUpdatesReduceFn, null);
    case 'Quiz':
      return [
        ['quiz_type', attrs.type],
        ['questions_count', attrs.questions_count],
        ['is_private', attrs.is_private],
      ].reduce(getMetadatUpdatesReduceFn, null);
    case 'QuizQuestion':
      return [
        ['question_type', attrs.type],
        ['duration', attrs.video && attrs.video.duration],
        ['is_private', attrs.is_private],
      ].reduce(getMetadatUpdatesReduceFn, null);
  }
}
function getMetadatUpdatesReduceFn(mem, [k, v]) {
  if (v !== undefined) {
    mem ||= {};
    mem[k] = v;
  }
  return mem;
}

export function getOCActivityMetadataLabel(activity) {
  const type = activity.activity_type;
  const meta = activity.activity_metadata;
  return (
    type === 'Quiz' ? pluralize(meta?.questions_count, 'Qn')
    : !meta?.reading_only && meta?.duration ?
      approximateDuration(meta.duration, { excludeDays: true, shortName: true }).join(' ')
    : false
  );
}

export function getActivityTypeConfig(activity) {
  const amd = activity.activity_metadata || {};

  if (activity.activity_type === 'Quiz') {
    return amd.quiz_type === 'practice_exam' ?
        {
          key: 'practice_exam',
          label: 'Mock Exam',
          color: 'yellow',
          icon: faClipboardListCheck,
          iconImg: getQuizIcon({ type: 'practice_exam' }),
        }
      : {
          key: 'quiz',
          label: 'Quiz',
          color: 'yellow',
          icon: faClipboardQuestion,
          iconImg: getQuizIcon({ type: 'quiz' }),
        };
  } else if (activity.activity_type === 'QuizQuestion') {
    return {
      key: 'practice_question',
      label: 'Practice Question',
      color: 'cyan',
      icon: faSealQuestion,
      iconImg: getLessonIcon({ type: 'practice_question' }),
    };
  }
  switch (amd.lesson_type) {
    case 'practice_question':
      return {
        key: 'practice_question',
        label: 'Practice Question',
        color: 'cyan',
        icon: faSealQuestion,
        iconImg: getLessonIcon({ type: 'practice_question' }),
      };
    case 'concept_clarifier':
      return {
        key: 'example_problem',
        label: 'Example Problem',
        color: 'secondary',
        icon: faChalkboardTeacher,
        iconImg: getLessonIcon({ type: 'concept_clarifier' }),
      };
    case 'cheatsheet':
      return {
        key: 'cheatsheet',
        label: 'Cheat Sheet',
        color: 'primary',
        icon: faFileCircleInfo,
        iconImg: getLessonIcon({ type: 'cheatsheet' }),
      };
    case 'intro':
      return {
        key: 'intro',
        label: 'Intro',
        color: 'primary',
        icon: faFlagCheckered,
        iconImg: getLessonIcon({ type: 'intro' }),
      };
  }

  return {
    key: 'theory',
    label: 'Theory Lesson',
    color: 'primary',
    icon: !amd.reading_only && !!amd.duration ? faClapperboardPlay : faLightbulbOn,
    iconImg: getLessonIcon({ type: 'theory', reading_only: amd.reading_only, video: !!amd.duration }),
  };
}

export function getActivityIcon(activity) {
  const amd = activity.activity_metadata || {};
  switch (activity.activity_type) {
    case 'CourseLesson':
      return getLessonIcon({ type: amd.lesson_type });
    case 'Quiz':
      return getQuizIcon({ type: amd.quiz_type });
    case 'QuizQuestion':
      return getLessonIcon({ type: 'practice_question' });
  }
}

export function getActivityUid(activity) {
  if (typeof activity.activity_id !== 'number') return null;
  switch (activity.activity_type) {
    case 'CourseLesson':
      return `CL-${activity.activity_id}`;
    case 'Quiz':
      return `QZ-${activity.activity_id}`;
    case 'QuizQuestion':
      return `QQ-${activity.activity_id}`;
  }
}

export function getDefaultActivityTitle(activityClass, activityType, position) {
  return [
    activityClass === 'CourseLesson' ? COURSE_LESSON_TYPES_CONFIG[activityType || 'theory'].label
    : activityClass === 'QuizQuestion' ? 'Practice Question'
    : QUIZ_TYPES_CONFIG[activityType || 'regular'].label,
    position,
  ].join(' ');
}

export function mapWithPositionOfType(activities, callback) {
  const ocTypeCounters = {};
  return activities.map((activity, ...rest) => {
    const amd = activity.activity_metadata || {};
    const type = amd.lesson_type || amd.quiz_type || activity.activity_type;
    return callback(activity, (ocTypeCounters[type] = (ocTypeCounters[type] || 0) + 1), ...rest);
  });
}

const USER_FIELDS = ['name', 'slug', 'avatar'];

const COURSE_LESSON_PARAMS = {
  fields: {
    course_lesson: [
      'type',
      'base_type',
      'title',
      'content',
      'reading_only',
      'has_video',
      'is_private',
      'user',
      'video_posters',
      'video_thumbnails',
      'video',
      'elements',
    ],
    user: USER_FIELDS,
  },
  include: ['user'],
};

const QQ_PREVIEW_FIELDS = [
  'type',
  'base_type',
  'description',
  'content',
  'hint',
  'is_private',
  'user',
  'has_video',
  'has_solution',
  'submission_stats',
  'current_user_submission',
  'intro_content',
  'stem_content',
  ...QUESTION_CONTENT_FIELDS,
];

export const QQ_FULL_FIELDS = [
  ...QQ_PREVIEW_FIELDS,
  'video',
  'video_elements',
  'solution',
  'custom_tags',
  'conclusion_content',
  'content_solution_highlights',
];
const QUIZ_PARAMS = {
  fields: {
    quiz: ['type', 'base_type', 'title', 'questions_count', 'user', 'is_private', 'settings', 'estimated_time'],
    user: USER_FIELDS,
  },
  include: ['user'],
};
export const ACTIVITY_TYPES_CONFIG = {
  CourseLesson: {
    label: 'Lesson',
    storeKey: 'courseLessons',
    fetchParams: {
      preview: COURSE_LESSON_PARAMS,
      full: COURSE_LESSON_PARAMS,
    },
  },
  Quiz: {
    label: 'Quiz',
    storeKey: 'quizzes',
    fetchParams: {
      preview: QUIZ_PARAMS,
      full: QUIZ_PARAMS,
    },
  },
  QuizQuestion: {
    label: 'Practice Question',
    storeKey: 'quizQuestions',
    fetchParams: {
      preview: {
        fields: {
          quiz_question: QQ_PREVIEW_FIELDS,
          user: USER_FIELDS,
        },
        include: ['current_user_submission', 'user'],
      },
      full: {
        fields: {
          quiz_question: QQ_FULL_FIELDS,
          user: USER_FIELDS,
        },
        include: ['current_user_submission', 'user'],
      },
    },
  },
};

export const CONTENT_EDITOR_PLUGINS = {
  equation: true,
  alignment: true,
  lineSpacing: true,
  link: true,
  image: true,
  tooltip: true,
  listTypes: true,
  table: true,
  headerTypes: [
    [
      'header-two',
      'Lesson Title',
      { blockProps: { alignment: 'center' }, styles: ['COLOR_PRIMARY'], htmlStyle: { color: palette.primary.main } },
    ],
    ['header-four', 'Subheader', { styles: ['COLOR_PRIMARY'], htmlStyle: { color: palette.primary.main } }],
  ],
  supSub: true,
  inlineStyles: true,
  colors: true,
  copyPaste: true,
  rawLatexInput: true,
};
